import { HOTEL_API_LANGUAGE, HOTEL_REVIEW_ERROR, HOTEL_SEARCH_ID_TYPE } from '@/src/constants/hotels'
import {
    CancellationPolicyRequestParams,
    CancellationPolicyResponse,
    CheckAvailabilityRequestParams,
    HotelByUniqueIdRequestParams,
    HotelReviewRequestParams,
    SelectHotelData,
    SelectHotelResponse
} from '@/src/types/hotels/details'
import { createApi } from '@reduxjs/toolkit/query/react'
import { createHotelBaseQuery } from './hotelBaseQuery'

// Hotels upstream (Flyin) review endpoints: selectHotel loads the property + room
// variances; cancellationPolicy is fetched lazily per rate when the user expands it.
export const hotelReviewApi = createApi({
    reducerPath: 'hotelReviewApi',
    baseQuery: createHotelBaseQuery(),

    tagTypes: ['HotelReview'],
    keepUnusedDataFor: 600,

    endpoints: builder => ({
        getHotelReview: builder.query<SelectHotelData, HotelReviewRequestParams>({
            query: ({ echoToken, htlUniqueId }) => ({
                url: '/app/hotel/selectHotel',
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
                body: new URLSearchParams({ echoToken, htlUniqueId }).toString()
            }),
            transformResponse: (response: SelectHotelResponse) => response.data,
            transformErrorResponse: (response, meta, arg) => ({
                status: response.status,
                error: HOTEL_REVIEW_ERROR,
                message: `Hotel review request failed with status ${response.status}`,
                htlUniqueId: arg.htlUniqueId,
                data: response.data || {}
            }),
            providesTags: (result, error, { htlUniqueId }) => [{ type: 'HotelReview', id: htlUniqueId }]
        }),

        // Path B: opened straight from the destination predictive, so there is no prior search
        // to select against. The Hotel-type search runs its own search upstream and returns the
        // same envelope as selectHotel — both end at the upstream's review builder — so the
        // response drops into the same view model.
        getHotelByUniqueId: builder.query<SelectHotelData, HotelByUniqueIdRequestParams>({
            query: params => ({
                url: `/app/hotel/searchHotels?${new URLSearchParams({
                    hotelSearchType: 'Hotel',
                    hotelId: params.hotelId,
                    uniqueId: params.hotelId,
                    hotelName: params.hotelName,
                    checkInDate: params.checkInDate,
                    checkOutDate: params.checkOutDate,
                    nights: params.nights,
                    noOfRooms: params.noOfRooms,
                    noOfAdults: params.noOfAdults,
                    noOfChildren: params.noOfChildren,
                    childrenAges: params.childrenAges,
                    language: HOTEL_API_LANGUAGE,
                    arg: 'newSearch',
                    api: 'true',
                    idType: HOTEL_SEARCH_ID_TYPE
                }).toString()}`
            }),
            transformResponse: (response: SelectHotelResponse) => response.data,
            providesTags: (result, error, params) => [{ type: 'HotelReview', id: params.hotelId }]
        }),

        // Re-prices the already-open property for new dates. Returns the same envelope as
        // selectHotel (fresh uuid + productId), so the caller can shadow the review data with
        // it instead of re-searching and re-navigating.
        checkHotelAvailability: builder.mutation<SelectHotelData, CheckAvailabilityRequestParams>({
            query: params => ({
                url: '/app/hotel/checkHotelAvailability',
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
                body: new URLSearchParams({
                    hotelSearchType: 'Hotel',
                    checkInDate: params.checkInDate,
                    checkOutDate: params.checkOutDate,
                    nights: String(params.nights),
                    noOfRooms: String(params.noOfRooms),
                    noOfAdults: params.noOfAdults,
                    noOfChildren: params.noOfChildren,
                    childrenAges: params.childrenAges,
                    hotelId: params.hotelId,
                    productId: params.productId,
                    language: HOTEL_API_LANGUAGE
                }).toString()
            }),
            transformResponse: (response: SelectHotelResponse) => response.data
        }),

        // Lazy per-rate cancellation policy. Body uses the `data[...]` bracket keys the upstream expects.
        getCancellationPolicy: builder.query<CancellationPolicyResponse, CancellationPolicyRequestParams>({
            query: ({ roomId, pid, uid, echoToken, index }) => ({
                url: '/app/hotel/cancellationPolicy',
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
                body: new URLSearchParams({
                    'data[roomId]': roomId,
                    'data[pid]': pid,
                    'data[uid]': uid,
                    'data[echoToken]': echoToken,
                    'data[index]': index
                }).toString()
            })
        })
    })
})

export const {
    useGetHotelReviewQuery,
    useGetHotelByUniqueIdQuery,
    useCheckHotelAvailabilityMutation,
    useLazyGetCancellationPolicyQuery
} = hotelReviewApi

export default hotelReviewApi
